import {
	exportImageData,
	ExportImageOptions,
	getExportImageSize,
	getYUVExportByteLength,
} from "@/core/export-image"
import type { PixelData } from "@/core/utils/image-data"
import { encodeJpegBytes, type EncodedJPEGData } from "@/core/encoders/jpeg-encoder"
import { AdjustmentsHistoryRecord, PhotoRecord } from "@/lib/catalog"
import { analytics } from "@/telemetry/amplitude"
import { ProcessingPerformance } from "@/telemetry/processing-performance"
import { atomWithReset, RESET } from "jotai/utils"
import toast from "react-hot-toast"
import {
	commitTaskPerformanceMeasurement,
	beginTaskPerformanceMeasurement,
} from "../debug/task-performance-state"
import { dialogsAtom, popDialog, replaceDialog } from "../dialogs-state"
import { store } from "../store"
import { currentStyleAtom, showAddEditsToStyleDialog } from "../style/style-state"
import {
	consumeExportCreditForPhotos,
	loadPreparedPhotoIntoEditorForExport,
	preparePhotoForExport,
	reloadCurrentPhotoIntoEditorForExport,
} from "./photo-exporting"
import {
	BeforeUploadResponseDTO,
	PhotoResponseDTO,
	PSApiOperations,
	StorePhotosRequestDTO,
	UploadRequestDTO,
} from "@/api/PSApiOperations"
import PQueue from "p-queue"
import { photosAtom, updatePhotos } from "../photo/photos-state"
import { ButtonContent, ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import {
	buildExportDetails,
	getExportDetailsSummary,
	shouldShowExportDetails,
	type ExportDetails,
} from "@/components/export-details/export-details-utils"
import { ExportDetailsView } from "@/components/export-details/ExportDetailsView"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { PublishLoadingDialog } from "@/components/Dialogs/PublishDialog"
import { ImageUploadService, uploadMetricsAtom } from "../Pixieset/ImageUploadService"
import { atom } from "jotai"
import { getImageInfo } from "../processing/image-info"
import { getEmbeddableAdjustments } from "@/core/adjustments"
import { JpegEncoder, type JpegYUVBufferLease } from "@/lib/jpeg/jpeg-encoder"
import { psGalleriesBaseUri, queryClient } from "@/api/constants"
import { getEditor } from "@/utils/useEditor"
import { isRealColorTag } from "@/utils/exif"
import { getExportCompleteEventProperties, getIsAIEditOnlyFromHistory } from "./export-event-utils"
import { isManualModeAtom, projectAtom } from "../project/projects-state"
import {
	isBrowserOffline,
	isNetworkConnectivityError,
	showActiveNetworkErrorToast,
} from "@/api/network-behavior"
import {
	handlePhotoExportError,
	showPhotoExportStorageErrorDialog,
	type PhotoExportStorageError,
} from "./photo-export-error"
import {
	isValidatedExportType,
	validateEncodedExportSizeAndReport,
} from "@/core/utils/encoded-size-check"
import { holdDisplayWakeLock } from "@/utils/display-wake-lock"
import { subscribeSystemSleepEvents } from "@/utils/system-sleep-events"
import {
	PhotoExportingBusyError,
	photoExportingSession,
	type PhotoExportingSession,
} from "./photo-exporting-session"
import { isAbortError } from "@/utils/error"
import { createElement } from "react"
import {
	cleanupPhotoOutputWorkers,
	preparePhotoOutputWorkers,
} from "./photo-output-worker-lifecycle"
import { PhotoOutputLookahead, shouldPrefetchPhotoOutputImport } from "./photo-output-lookahead"
import { AsyncSemaphore, type AsyncSemaphoreLease } from "@/utils/async-semaphore"
import { estimatePublishTimeRemaining } from "./publish-time-remaining"
import { getPublishPhotoRanks } from "./photo-publishing-rank"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { getPublishExportSettingsKey } from "../Pixieset/publish-export-settings"
import { isPixiesetJpegOverUploadLimit } from "../Pixieset/upload-limits"

const imageUploadService = new ImageUploadService()

const cancelExportAtom = atomWithReset(false)
export const publishCancelRequestedAtom = cancelExportAtom
const PUBLISH_CANCELING_DIALOG_TITLE = "Cancelling publish..." // i18n-ignore — used as dialog identity check
type PublishCancelReason =
	| "user-canceled"
	| "storage-error"
	| "offline-network"
	| "credit-consumption-failure"
let pendingPublishCancelReason: PublishCancelReason | undefined
let pendingPublishCreditFailureMessage: string | undefined
let pendingPublishStorageError: PhotoExportStorageError | undefined
const totalToPublishAtom = atomWithReset(0)
const photosExportedAtom = atomWithReset<string[]>([])
const photosUploadedAtom = atomWithReset<Set<UploadResult>>(new Set())
const photosPublishedAtom = atomWithReset<Set<string>>(new Set())
const photosFailedAtom = atomWithReset<string[]>([])
const photosDeletedAtom = atomWithReset(0)

const PUBLISH_BATCH_SIZE = 5
const PUBLISH_UPLOAD_CONCURRENCY = 6
const PUBLISH_PIPELINE_CAPACITY = 20

export interface PublishCollectionSnapshot {
	id: string | null
	name: string | null
}

export const publishProgressAtom = atom((get) => {
	const total = get(totalToPublishAtom)
	const exports = get(photosExportedAtom)
	const uploads = get(photosUploadedAtom)
	const failed = get(photosFailedAtom)
	const { totalTime } = get(uploadMetricsAtom)
	const uploadedFilenames = new Set(Array.from(uploads, (upload) => upload.filename))
	const failedBeforeUpload = failed.filter((filename) => !uploadedFilenames.has(filename)).length
	const estimatedTimeRemaining = estimatePublishTimeRemaining({
		totalPhotos: total,
		finishedPhotos: uploads.size + failedBeforeUpload,
		uploadedPhotos: uploads.size,
		elapsedSeconds: totalTime,
		uploadConcurrency: PUBLISH_UPLOAD_CONCURRENCY,
	})
	return {
		exports: exports.length,
		totalToExports: total,
		uploads: uploads.size,
		totalToUpload: total,
		total: total * 2,
		totalProgress: exports.length + uploads.size,
		timeRemaining: estimatedTimeRemaining,
	}
})

export const cancelPublish = (dismissDialog: boolean = true) => {
	if (dismissDialog) {
		popDialog()
	}

	imageUploadService.cancel()
	store.set(cancelExportAtom, true)
	photoExportingSession.cancelCurrent("publish")
}

function showPublishCancelingDialog() {
	replaceDialog(LoadingDialog, { title: PUBLISH_CANCELING_DIALOG_TITLE })
}

export function cancelPublishFromDialog() {
	imageUploadService.cancel()
	pendingPublishCancelReason = "user-canceled"
	store.set(cancelExportAtom, true)
	showPublishCancelingDialog()
	photoExportingSession.cancelCurrent("publish")
}

function getPhotoKey(filename: string, galleryId: string) {
	const key = `${galleryId.toString().split("").reverse().join("")}/${filename}`
	return key
}

function reset() {
	store.set(cancelExportAtom, RESET)
	store.set(photosExportedAtom, RESET)
	store.set(photosUploadedAtom, RESET)
	store.set(photosPublishedAtom, RESET)
	store.set(photosFailedAtom, RESET)
	store.set(totalToPublishAtom, RESET)
	store.set(photosDeletedAtom, RESET)
	imageUploadService.reset()
}

function popPublishSessionDialogIfActive() {
	const [component, props] = store.get(dialogsAtom).at(-1) ?? []
	if (
		component === PublishLoadingDialog ||
		(component === LoadingDialog &&
			(props as { title?: string } | undefined)?.title === PUBLISH_CANCELING_DIALOG_TITLE)
	) {
		popDialog()
	}
}

export async function beginPublishSession(total: number) {
	let session: PhotoExportingSession
	try {
		session = await photoExportingSession.beginSession("publish", { rejectIfActive: true })
	} catch (error) {
		if (error instanceof PhotoExportingBusyError) {
			console.warn(
				"[Publish] publish request ignored because export/publish is already active",
			)
			return
		}
		throw error
	}

	reset()
	store.set(totalToPublishAtom, total)
	replaceDialog(PublishLoadingDialog)
	return session
}

export async function finishPreparedPublishSession(
	session: PhotoExportingSession,
	options?: { canceled?: boolean; dismissDialog?: boolean; resetState?: boolean },
) {
	if (options?.canceled) {
		await reloadCurrentPhotoIntoEditorForExport(session, { allowCanceled: true })
	}

	if (options?.resetState ?? true) {
		reset()
	}

	photoExportingSession.finishSession(session)

	if (options?.dismissDialog ?? true) {
		popPublishSessionDialogIfActive()
	}
}

export async function startPublishPhotos(
	photos: PhotoRecord[],
	galleryId: string,
	uploadRequestResponse_: BeforeUploadResponseDTO | undefined,
	options: ExportImageOptions,
	photosToDelete: PhotoResponseDTO[],
	collection: PublishCollectionSnapshot,
	publishFilenames: Record<string, string>,
	preparedSession?: PhotoExportingSession,
	previouslyAssignedRanks?: ReadonlyMap<string, number>,
) {
	const total = photos?.length ?? 0
	let releasePublishWakeLock: (() => void) | undefined
	let unsubscribeSleepEvents: (() => void) | undefined
	const currentProject = store.get(projectAtom)
	const isSampleProject = currentProject?.isSampleProject === true
	const aiEditOnlyByPhotoId = new Map<string, boolean>()
	const historyRecordByPhotoId = new Map<string, AdjustmentsHistoryRecord | undefined>()
	const photoById = new Map<string, PhotoRecord>(photos.map((photo) => [photo.id, photo]))
	let publishPhotoRanksById = previouslyAssignedRanks
	const failedPhotoIds = new Set<string>()
	const oversizePhotoIds = new Set<string>()
	const publishExportSettingsKey = getPublishExportSettingsKey(options)
	pendingPublishCancelReason = undefined
	pendingPublishCreditFailureMessage = undefined
	pendingPublishStorageError = undefined
	const getPublishFilename = (photo: PhotoRecord) => publishFilenames[photo.id] ?? photo.fileName
	const allPublishFilenames = photos.map(getPublishFilename)
	const getFilenamesForPhotoIds = (photoIds: Iterable<string>) =>
		Array.from(
			photoIds,
			(photoId) => publishFilenames[photoId] ?? photoById.get(photoId)?.fileName ?? photoId,
		)
	const getPublishDetails = (options?: {
		failedFilenames?: string[]
		failedPhotoIds?: Iterable<string>
	}): ExportDetails => {
		return buildExportDetails({
			flow: "publish",
			completedFilenames: Array.from(store.get(photosPublishedAtom)),
			allFilenames: allPublishFilenames,
			oversizeFilenames: getFilenamesForPhotoIds(oversizePhotoIds),
			failedFilenames: options?.failedPhotoIds
				? getFilenamesForPhotoIds(options.failedPhotoIds)
				: options?.failedFilenames,
		})
	}
	const getNotPublishedPhotoIds = () => {
		const publishedFilenames = store.get(photosPublishedAtom)
		return photos
			.filter(
				(photo) =>
					!publishedFilenames.has(getPublishFilename(photo)) &&
					!oversizePhotoIds.has(photo.id),
			)
			.map((photo) => photo.id)
	}
	const requestUploadForPublishRetry = async (
		retryPhotos: PhotoRecord[],
	): Promise<BeforeUploadResponseDTO | undefined> => {
		const uploadRequest: UploadRequestDTO = {
			files: retryPhotos.map((photo) => ({
				name: getPublishFilename(photo),
				type: "image/jpeg",
				size: 1,
			})),
		}

		try {
			const uploadRequestResponse = await PSApiOperations.requestUpload({
				galleryId,
				requestBody: uploadRequest,
			})

			if (uploadRequestResponse.photos_storage_status !== "available") {
				throw new Error("Photo storage not available.")
			}

			return uploadRequestResponse
		} catch (error) {
			if (isNetworkConnectivityError(error)) {
				const retryPhotoIds = retryPhotos.map((photo) => photo.id)
				showPublishNetworkFailureDialog(
					getPublishDetails({
						failedPhotoIds: retryPhotoIds,
					}),
					retryPhotoIds,
				)
				return undefined
			}

			throw error
		}
	}
	const getViewOnPixiesetAction = () => ({
		title: i18n.t("store:photoPublishing.viewOnPixieset"),
		disablePopDialog: true,
		handler: () => {
			if (collection.id) {
				window.open(`${psGalleriesBaseUri}/collections/${collection.id}`, "_blank")
			}
		},
	})
	const okAndResetAction = {
		title: i18n.t("common:action.ok"),
		handler: () => {
			reset()
		},
	}
	const showPublishResultDialog = (options: {
		title: string
		message: string
		details?: ExportDetails
		trackingViewName: string
		primary?: ButtonContent
		secondary?: ButtonContent
		retryHandler?: () => void
		retrySecondary?: ButtonContent
	}) => {
		replaceDialog(ConfirmDialog, {
			canClose: true,
			title: options.title,
			message: options.message,
			primary: options.retryHandler
				? {
						title: i18n.t("common:action.retry"),
						handler: options.retryHandler,
					}
				: options.primary,
			secondary: options.retryHandler ? options.retrySecondary : options.secondary,
			embeddedComponent:
				options.details && shouldShowExportDetails(options.details)
					? createElement(ExportDetailsView, { details: options.details })
					: undefined,
			trackingViewName: options.trackingViewName,
		})
	}
	const showPublishNetworkFailureDialog = (
		details: ExportDetails,
		retryPhotoIds: string[] = Array.from(failedPhotoIds),
	) => {
		const retryHandler =
			retryPhotoIds.length > 0 ? () => retryFailedPublishes(retryPhotoIds) : undefined
		const viewOnPixiesetAction =
			details.completedFilenames.length > 0 ? getViewOnPixiesetAction() : undefined

		showPublishResultDialog({
			title: i18n.t("store:photoPublishing.publishFailedTitle"),
			message:
				i18n.t("store:photoPublishing.publishFailedMessage", {
					defaultValue: i18n.t("store:storeClientGallery.publishFailedMessage"),
				}) +
				" " +
				getExportDetailsSummary(details),
			details,
			primary: viewOnPixiesetAction ?? okAndResetAction,
			retryHandler,
			retrySecondary: viewOnPixiesetAction ?? okAndResetAction,
			trackingViewName: "publish_network_connectivity_failed",
		})
	}
	const requestOfflineNetworkPublishStop = () => {
		if (pendingPublishCancelReason === "offline-network") {
			return false
		}

		pendingPublishCancelReason = "offline-network"
		cancelPublish(false)
		return true
	}

	const retryFailedPublishes = (photoIds: string[]) => {
		const retryPhotos = photoIds.flatMap((photoId) => {
			const photo = photoById.get(photoId)
			return photo ? [photo] : []
		})

		if (retryPhotos.length === 0) {
			return
		}

		void (async () => {
			const retrySession = await beginPublishSession(retryPhotos.length)
			if (!retrySession) {
				return
			}

			try {
				const uploadRequestResponse = await requestUploadForPublishRetry(retryPhotos)
				if (!uploadRequestResponse) {
					await finishPreparedPublishSession(retrySession, { dismissDialog: false })
					return
				}

				retrySession.throwIfCanceled()

				// An immediate retry keeps the ranks assigned by the original publish attempt.
				void startPublishPhotos(
					retryPhotos,
					galleryId,
					uploadRequestResponse,
					options,
					[],
					collection,
					publishFilenames,
					retrySession,
					publishPhotoRanksById,
				).catch((error) => {
					if (isAbortError(error)) {
						console.warn("[Publish] retry flow canceled", error)
						return
					}

					console.error("[Publish] retry flow failed", error)
				})
			} catch (error) {
				if (retrySession.isCanceled || isAbortError(error)) {
					console.warn("[Publish] retry preparation canceled", error)
					await finishPreparedPublishSession(retrySession, { canceled: true })
					return
				}

				await finishPreparedPublishSession(retrySession)
				toast.error(i18n.t("store:photoPublishing.failedRetryPublish"))
				console.error("[Publish] retry preparation failed", error)
			}
		})()
	}

	const showCanceledPublishDialog = (details: ExportDetails) => {
		showPublishResultDialog({
			title: i18n.t("store:photoPublishing.publishCanceledTitle"),
			message: getExportDetailsSummary(details), // i18n-ignore — dynamic summary
			details,
			primary: { title: i18n.t("common:action.ok") },
			trackingViewName: "publish_canceled_dialog",
		})
	}
	const showPublishCreditConsumptionFailureDialog = (
		failureMessage: string,
		details: ExportDetails,
		retryPhotoIds: string[],
	) => {
		const retryHandler =
			retryPhotoIds.length > 0 ? () => retryFailedPublishes(retryPhotoIds) : undefined

		showPublishResultDialog({
			title: i18n.t("store:photoPublishing.publishIncompleteTitle"),
			message: `${failureMessage} ${getExportDetailsSummary(details)}`, // i18n-ignore — dynamic
			details,
			primary: { title: i18n.t("common:action.ok") },
			retryHandler,
			retrySecondary: { title: i18n.t("common:action.ok") },
			trackingViewName: "publish_credit_consumption_failed",
		})
	}

	const getAnalyticsInfo = (extras = {}) => {
		const info = {
			projectId: options.projectId,
			styleId: store.get(currentStyleAtom)?.styleId,
			totalCount: total,
		}

		return { ...info, ...extras }
	}

	const session = preparedSession ?? (await beginPublishSession(total))
	if (!session) {
		return
	}
	const shouldReloadBeforePublishResult = Boolean(uploadRequestResponse_ && photos.length > 0)
	const performance = new ProcessingPerformance("export", {
		projectId: options.projectId,
		styleId: store.get(currentStyleAtom)?.styleId,
		outputFormat: options.type,
		destination: "pixieset",
	})
	const storeTasks: Promise<unknown>[] = []
	let flushUploadedPhotosIfNeeded: (allowCanceled?: boolean) => void = () => undefined
	let didStartPhotoOutputWorkerSetup = false
	let didFinishPhotoOutput = false
	let showPostCleanupDialog: (() => void) | undefined

	const finishPublish = (finishOptions?: { didReloadCurrentPhoto?: boolean }) => {
		if (!session.isCurrent() || session.isCanceled) {
			return
		}
		if (shouldReloadBeforePublishResult && !finishOptions?.didReloadCurrentPhoto) {
			return
		}

		commitTaskPerformanceMeasurement("publish")

		const successCount = store.get(photosPublishedAtom).size
		const deletedCount = store.get(photosDeletedAtom)
		const isManualMode = store.get(isManualModeAtom)

		const retryPhotoIds = Array.from(failedPhotoIds)
		const details = getPublishDetails({
			failedFilenames: retryPhotoIds.length > 0 ? undefined : store.get(photosFailedAtom),
			failedPhotoIds: retryPhotoIds.length > 0 ? retryPhotoIds : undefined,
		})
		const failedCount = details.failedFilenames.length
		const oversizeCount = details.oversizeFilenames.length
		const retryHandler =
			retryPhotoIds.length > 0 ? () => retryFailedPublishes(retryPhotoIds) : undefined
		let message = ""
		if (successCount > 0 && deletedCount > 0) {
			message += `Published ${
				successCount > 1 ? `${successCount} photos` : "a photo"
			} and deleted ${deletedCount > 1 ? `${deletedCount} photos` : "a photo"} successfully. `
		} else if (successCount > 0) {
			message += `Published ${
				successCount > 1 ? `${successCount} photos` : "a photo"
			} successfully. `
		} else if (deletedCount > 0) {
			message += `Deleted ${
				deletedCount > 1 ? `${deletedCount} photos` : "a photo"
			} successfully. `
		}

		if (failedCount > 0) {
			message += `Failed to publish ${
				failedCount > 1 ? `${failedCount} photos` : "a photo"
			}. `
		}
		if (oversizeCount > 0) {
			message += `${
				oversizeCount > 1 ? `${oversizeCount} photos were` : "A photo was"
			} over Pixieset's 100 MiB per-file limit and could not be published. `
		}

		if (successCount > 0 && failedCount > 0) {
			message += "View the collection on Pixieset or retry the failed photos."
		} else if (!isManualMode && oversizeCount === 0) {
			message += "Train AI Style using your final edits, or view it on Pixieset."
		}

		const deletedOnly = successCount === 0 && deletedCount > 0
		const hasSuccessUploads = successCount > 0
		const secondaryAction =
			deletedOnly || isManualMode || !hasSuccessUploads
				? undefined
				: {
						title: i18n.t("store:photoPublishing.trainMyAiStyle"),
						handler: () => {
							showAddEditsToStyleDialog()
						},
					}

		const primaryAction = hasSuccessUploads ? getViewOnPixiesetAction() : undefined
		showPublishResultDialog({
			title: i18n.t("store:photoPublishing.publishCompleteTitle"),
			message,
			details,
			primary: primaryAction ?? okAndResetAction,
			secondary: secondaryAction,
			retryHandler,
			retrySecondary: hasSuccessUploads
				? primaryAction
				: { title: i18n.t("common:action.ok") },
			trackingViewName: "publish_complete",
		})

		const aiEditOnlyCount = photos.filter(
			(photo) => aiEditOnlyByPhotoId.get(photo.id) === true,
		).length

		analytics.trackEvent({
			label: "export_complete",
			type: "event",
			customProperties: {
				...getExportCompleteEventProperties({
					destination: "pixieset",
					projectId: options.projectId,
					exportedPhotos: photos,
					completedCount: successCount,
					aiEditOnlyCount,
					collectionId: collection.id,
					collectionName: collection.name,
				}),
				oversizeCount,
			},
		})

		reset()
	}

	try {
		session.throwIfCanceled()

		const countDeletedPhotos = photosToDelete.length
		if (countDeletedPhotos > 0) {
			const photoIds = photosToDelete.map((photo) => photo.id)
			await PSApiOperations.deletePhotos({
				galleryId,
				photoIds,
			})
			void queryClient.invalidateQueries({ queryKey: ["galleryPhotos"] })
			store.set(photosDeletedAtom, countDeletedPhotos)
		}

		session.throwIfCanceled()

		if (!uploadRequestResponse_) {
			if (countDeletedPhotos > 0) {
				finishPublish()
			}
			return
		}

		const uploadRequestResponse: BeforeUploadResponseDTO = uploadRequestResponse_

		if (!photos.length || photos.length === 0) {
			return
		}

		// getGalleryUpdates preserves the current editor display order in photos. Assign ranks
		// from this snapshot before parallel exports so completion and batch order cannot change it.
		publishPhotoRanksById = getPublishPhotoRanks(
			photos.map((photo) => photo.id),
			uploadRequestResponse.photo_max_rank,
			publishPhotoRanksById,
		)

		const storePhotosIfNeeded = (allowCanceled = false) => {
			if (!session.isCurrent() || (session.isCanceled && !allowCanceled)) {
				return
			}

			const photosUploaded = store.get(photosUploadedAtom)
			const photosPublished = store.get(photosPublishedAtom)
			const photosFailed = store.get(photosFailedAtom)
			const isCompleted = photosFailed.length + photosUploaded.size === total

			// Store in batches so we do not lose progress. Also store if all files are uploaded.
			if (isCompleted || photosUploaded.size - photosPublished.size >= PUBLISH_BATCH_SIZE) {
				const uploadsToStore: UploadResult[] = Array.from(photosUploaded).filter(
					(result) => !photosPublished.has(result.filename),
				)

				if (uploadsToStore.length === 0) {
					if (!session.isCanceled) {
						finishPublish()
					}
					return
				}

				// Upload status first so we do not store duplicates.
				store.set(
					photosPublishedAtom,
					(prev) => new Set([...prev, ...uploadsToStore.map((u) => u.filename)]),
				)

				const storeTask = storePhotos(uploadsToStore, galleryId, publishExportSettingsKey)
					.then(() => {
						for (const upload of uploadsToStore) {
							performance.processed(photoById.get(upload.photoId)?.fileName)
						}
						if (!session.isCurrent()) {
							return
						}

						const creditsByPhoto = new Map<
							PhotoRecord,
							AdjustmentsHistoryRecord | undefined
						>()
						for (const upload of uploadsToStore) {
							const photo = photoById.get(upload.photoId)
							if (!photo) {
								continue
							}
							creditsByPhoto.set(photo, historyRecordByPhotoId.get(photo.id))
						}

						if (creditsByPhoto.size > 0) {
							void session.trackTask(
								consumeExportCreditForPhotos(creditsByPhoto, options.projectId, {
									onFailure: (failureMessage) => {
										if (!session.isCurrent() || session.isCanceled) {
											return
										}
										pendingPublishCancelReason = "credit-consumption-failure"
										pendingPublishCreditFailureMessage = failureMessage
										cancelPublish(false)
									},
									retryWhenPageBecomesVisible: true,
									skipCreditConsumption: isSampleProject,
								}),
							)
						}
					})
					.catch((error) => {
						if (!isAbortError(error)) {
							uploadsToStore.forEach(() => performance.failed())
						}
						if (!session.isCurrent() || (session.isCanceled && !allowCanceled)) {
							return
						}
						if (isAbortError(error)) {
							console.warn("[Publish] store photos task canceled", error)
							return
						}

						console.error("Failed to store photos", error)
						const failedUploads = uploadsToStore
						const failedFilenames = failedUploads.map((u) => u.filename)
						failedUploads.forEach((upload) => failedPhotoIds.add(upload.photoId))
						store.set(photosPublishedAtom, (prev) => {
							const revertPublished = new Set(prev)
							failedFilenames.forEach((filename) => revertPublished.delete(filename))
							return revertPublished
						})
						store.set(photosFailedAtom, (prev) => {
							const failedSet = new Set(prev)
							failedFilenames.forEach((filename) => failedSet.add(filename))
							return Array.from(failedSet)
						})

						const isNetworkError = isNetworkConnectivityError(error)
						if (isNetworkError && isBrowserOffline()) {
							requestOfflineNetworkPublishStop()
						} else if (isNetworkError) {
							showActiveNetworkErrorToast()
						}
					})
					.finally(() => {
						if (isCompleted && !session.isCanceled) {
							finishPublish()
						}
					})

				storeTasks.push(storeTask)
				void session.trackTask(storeTask)
			}
		}

		flushUploadedPhotosIfNeeded = storePhotosIfNeeded
		performance.start()

		const { queue, encodeQueue, uploadQueue } = batchPublishPhotos({
			session,
			photos,
			galleryId,
			options,
			uploadRequestResponse,
			publishFilenames,
			photoRanksById: publishPhotoRanksById,
			onHistoryRecordLoaded: (photo, historyRecord) => {
				if (!session.isCurrent() || session.isCanceled) {
					return
				}
				aiEditOnlyByPhotoId.set(photo.id, getIsAIEditOnlyFromHistory(historyRecord))
				historyRecordByPhotoId.set(photo.id, historyRecord)
			},
			successHandler: (result) => {
				if (!session.isCurrent() || session.isCanceled) {
					return
				}
				store.set(photosUploadedAtom, (prev) => {
					return new Set([...prev, result])
				})
				storePhotosIfNeeded()
			},
			errorHandler: (failed, error) => {
				if (!session.isCurrent() || session.isCanceled) {
					return
				}
				if (!failedPhotoIds.has(failed.photoId)) performance.failed()
				failedPhotoIds.add(failed.photoId)
				store.set(photosFailedAtom, (prev) => {
					return [...prev, failed.filename]
				})
				const isNetworkError = isNetworkConnectivityError(error)
				if (isNetworkError && isBrowserOffline()) {
					requestOfflineNetworkPublishStop()
				} else if (isNetworkError) {
					showActiveNetworkErrorToast()
				}
				storePhotosIfNeeded()
			},
			oversizeHandler: (failed) => {
				if (!session.isCurrent() || session.isCanceled) {
					return
				}
				if (!oversizePhotoIds.has(failed.photoId)) performance.failed()
				oversizePhotoIds.add(failed.photoId)
				store.set(photosFailedAtom, (prev) => [...prev, failed.filename])
				storePhotosIfNeeded()
			},
		})

		session.trackQueue(queue)
		session.trackQueue(encodeQueue)
		session.trackQueue(uploadQueue)
		session.onCancel(() => {
			imageUploadService.cancel()
		})

		session.throwIfCanceled()

		// Resetting again in case of previous lingering publish.
		reset()
		if (countDeletedPhotos > 0) {
			store.set(photosDeletedAtom, countDeletedPhotos)
		}

		store.set(totalToPublishAtom, total)
		replaceDialog(PublishLoadingDialog)

		// If there is only 1 item in the queue, onIdle fires immediately and wont wait for errors.
		if (total === 1) {
			queue.on("error", () => {
				if (!session.isCanceled) {
					toast.error(`Failed to export photo: ${Object.values(photos)[0]?.fileName}`)
				}
			})
		}

		// If setup is canceled halfway through, cleanup still needs to resume queues
		// that may already have been paused.
		didStartPhotoOutputWorkerSetup = true
		await preparePhotoOutputWorkers("publish", session)

		beginTaskPerformanceMeasurement("publish")

		unsubscribeSleepEvents = subscribeSystemSleepEvents({
			onPause: () => {
				if (!store.get(cancelExportAtom)) {
					console.log("[SleepResume][Publish] Pausing publish queues due to sleep")
					queue.pause()
					encodeQueue.pause()
				}
			},
			onResume: () => {
				if (!store.get(cancelExportAtom)) {
					console.log("[SleepResume][Publish] Resuming publish queues after wake")
					queue.start()
					encodeQueue.start()
				}
			},
		})

		console.log("[SleepResume][Publish] Acquiring wake lock")
		releasePublishWakeLock = holdDisplayWakeLock()
		queue.start()
		analytics.trackEvent({
			label: "export_start",
			type: "event",
			customProperties: getAnalyticsInfo(false),
		})

		await queue.onIdle()
		await encodeQueue.onIdle()
		await uploadQueue.onIdle()
		storePhotosIfNeeded(true)
		await session.waitUnlessCanceled(session.drainTrackedTasks())

		didFinishPhotoOutput = true
	} finally {
		await session.releaseAllLeases()
		if (releasePublishWakeLock) {
			console.log("[SleepResume][Publish] Releasing wake lock")
			releasePublishWakeLock()
			releasePublishWakeLock = undefined
		}
		if (unsubscribeSleepEvents) {
			console.log("[SleepResume][Publish] Unsubscribing sleep event listeners")
			unsubscribeSleepEvents()
			unsubscribeSleepEvents = undefined
		}

		if (session.isCanceled) {
			const cancelReason = pendingPublishCancelReason
			const creditFailureMessage = pendingPublishCreditFailureMessage
			const storageError = pendingPublishStorageError
			// Storage errors stop local export/encode work, but already uploaded photos can
			// still be stored so we preserve publish progress when possible.
			if (cancelReason === "storage-error") {
				flushUploadedPhotosIfNeeded(true)
			}

			// Capture retry targets and details before reset clears publish progress.
			const notPublishedPhotoIds = getNotPublishedPhotoIds()
			const canceledDetails = getPublishDetails()
			await reloadCurrentPhotoIntoEditorForExport(session, { allowCanceled: true })
			reset()
			if (cancelReason === "user-canceled") {
				showPostCleanupDialog = () => showCanceledPublishDialog(canceledDetails)
			} else if (cancelReason === "storage-error" && storageError) {
				const retryHandler =
					notPublishedPhotoIds.length > 0
						? () => retryFailedPublishes(notPublishedPhotoIds)
						: undefined
				showPostCleanupDialog = () =>
					showPhotoExportStorageErrorDialog({
						flow: "publish",
						storageError,
						storageErrorDetails: canceledDetails,
						retryHandler,
					})
			} else if (cancelReason === "offline-network") {
				showPostCleanupDialog = () =>
					showPublishNetworkFailureDialog(canceledDetails, notPublishedPhotoIds)
			} else if (cancelReason === "credit-consumption-failure" && creditFailureMessage) {
				showPostCleanupDialog = () =>
					showPublishCreditConsumptionFailureDialog(
						creditFailureMessage,
						canceledDetails,
						notPublishedPhotoIds,
					)
			}
		} else if (didFinishPhotoOutput) {
			await reloadCurrentPhotoIntoEditorForExport(session)
			finishPublish({ didReloadCurrentPhoto: true })
		}

		if (didStartPhotoOutputWorkerSetup) {
			cleanupPhotoOutputWorkers()
		}

		photoExportingSession.finishSession(session)
		// A canceled publish may still finish storing photos that were already uploaded.
		void Promise.allSettled(storeTasks).then(() =>
			performance.finish(
				session.isCanceled ? "canceled" : didFinishPhotoOutput ? "completed" : "failed",
			),
		)
		if (session.isCanceled) {
			popPublishSessionDialogIfActive()
		}
		pendingPublishCancelReason = undefined
		pendingPublishCreditFailureMessage = undefined
		pendingPublishStorageError = undefined
		showPostCleanupDialog?.()
	}
}

interface UploadResult {
	filename: string
	size: number
	photoId: string
	rank: number
}

function batchPublishPhotos(props: {
	session: PhotoExportingSession
	photos: PhotoRecord[]
	galleryId: string
	options: ExportImageOptions
	uploadRequestResponse: BeforeUploadResponseDTO
	publishFilenames: Record<string, string>
	photoRanksById: ReadonlyMap<string, number>
	onHistoryRecordLoaded?: (photo: PhotoRecord, historyRecord?: AdjustmentsHistoryRecord) => void
	successHandler: (result: UploadResult) => void
	errorHandler: (failed: Pick<UploadResult, "filename" | "photoId">, error?: unknown) => void
	oversizeHandler: (failed: Pick<UploadResult, "filename" | "photoId">) => void
}): {
	queue: PQueue
	encodeQueue: PQueue
	uploadQueue: PQueue
} {
	const {
		session,
		photos,
		options,
		uploadRequestResponse,
		publishFilenames,
		photoRanksById,
		onHistoryRecordLoaded,
		successHandler,
		errorHandler,
		oversizeHandler,
		galleryId,
	} = props

	const queue = new PQueue({ concurrency: 1, autoStart: false })
	const encodeQueue = new PQueue({ concurrency: 1, autoStart: true })
	const uploadQueue = new PQueue({
		concurrency: PUBLISH_UPLOAD_CONCURRENCY,
		autoStart: true,
	})
	// The JPEG encoder already limits the large YUV render buffer to one lease.
	// This separate bound keeps uploads fed while retaining at most 20 photos,
	// predominantly the much smaller encoded JPEGs, instead of the entire gallery.
	const publishPipelineCapacity = new AsyncSemaphore(PUBLISH_PIPELINE_CAPACITY)
	const shouldPrefetchImport = shouldPrefetchPhotoOutputImport(options)
	const importLookahead = session.trackLease(
		new PhotoOutputLookahead(photos, (photo) => preparePhotoForExport(photo, session.signal)),
	)

	const schedulePublishUpload = (
		photo: PhotoRecord,
		filename: string,
		rank: number,
		encodedImageData: EncodedJPEGData,
		pipelineLease: AsyncSemaphoreLease,
	) => {
		const encodedByteLength = encodedImageData.bytes.byteLength

		const uploadTask = uploadQueue
			.add(async () => {
				session.throwIfCanceled()
				await imageUploadService.uploadImageBytes(
					encodedImageData,
					getPhotoKey(filename, galleryId),
					filename,
					uploadRequestResponse,
					(filename) => {
						if (!session.isCurrent() || session.isCanceled) {
							return
						}
						successHandler({
							filename,
							photoId: photo.id,
							size: encodedByteLength,
							rank,
						})
					},
					(filename, err) => {
						if (!session.isCurrent() || session.isCanceled) {
							return
						}
						if (isAbortError(err)) {
							console.warn("[Publish] upload canceled", filename, err)
							return
						}
						console.error("[Publish] upload error", filename, err)
						errorHandler({ filename, photoId: photo.id }, err)
					},
					{ signal: session.signal },
				)
			})
			.catch((err) => {
				if (session.isCanceled) {
					return
				}
				if (isAbortError(err)) {
					console.warn("[Publish] upload queue task canceled", filename, err)
					return
				}
				console.error("[Publish] upload queue error", filename, err)
			})
			.finally(() => {
				void session.releaseLease(pipelineLease)
			})

		void uploadTask
	}

	const handlePublishPhotoError = (photo: PhotoRecord, err: unknown) => {
		if (session.isCanceled) {
			return
		}
		if (isAbortError(err)) {
			console.warn("[Publish] export/encode task canceled", photo.fileName, err)
			return
		}

		handlePhotoExportError({
			error: err,
			photoId: photo.id,
			flow: "publish",
			onStorageError: (storageError) => {
				pendingPublishCancelReason = "storage-error"
				pendingPublishStorageError = storageError
				cancelPublish(false)
			},
		})

		console.error("[Publish Error] Failed to export or encode photo", photo.fileName, err)
		errorHandler(
			{
				filename: publishFilenames[photo.id] ?? photo.fileName,
				photoId: photo.id,
			},
			err,
		)
	}

	photos.forEach((photo, photoIndex) => {
		queue
			.add(async () => {
				const filename = publishFilenames[photo.id]
				if (!filename) {
					throw new Error("No publish filename found")
				}
				const rank = photoRanksById.get(photo.id)
				if (rank === undefined) {
					throw new Error("No publish rank found")
				}

				const pipelineLease = session.trackLease(
					await publishPipelineCapacity.acquire(session.signal),
				)
				let didTransferPipelineLease = false

				try {
					session.throwIfCanceled()
					const preparedPhoto = await importLookahead.take(photoIndex)
					const exportedTask = photoExportingSession.withRenderer(session, async () => {
						getEditor().renderer.cleanup()
						const result = await loadPreparedPhotoIntoEditorForExport(preparedPhoto)
						if (shouldPrefetchImport) {
							importLookahead.prefetch(photoIndex + 1)
						}
						const { adjustments, metadata, historyRecord } = result
						onHistoryRecordLoaded?.(photo, historyRecord)

						const editor = getEditor()
						let imageData: PixelData
						let yuvBufferLease: JpegYUVBufferLease | undefined
						let didExportImageData = false
						try {
							if (options.type === "jpg" && options.format === "yuv") {
								const [width, height] = getExportImageSize(
									editor.photo.imageState.croppedImageSize,
									options,
								)
								const byteLength = getYUVExportByteLength(width, height)
								yuvBufferLease = session.trackLease(
									await JpegEncoder.acquireYUVBuffer(byteLength),
								)
								session.throwIfCanceled()
								imageData = await exportImageData(editor, {
									...options,
									bufferView: yuvBufferLease.data,
								})
							} else {
								imageData = await exportImageData(editor, { ...options })
							}
							didExportImageData = true
						} finally {
							if (!didExportImageData) {
								await session.releaseLease(yuvBufferLease)
							}
						}

						store.set(photosExportedAtom, (prev) => [...prev, photo.fileName])

						const leaseForEncode = yuvBufferLease
						yuvBufferLease = undefined

						return {
							adjustments,
							imageData,
							leaseForEncode,
							metadata,
						}
					})
					const exported = await (async () => {
						try {
							return await exportedTask
						} finally {
							await preparedPhoto.dispose()
						}
					})()

					let didQueueEncode = false
					try {
						const imageInfo = getImageInfo(photo.id)
						const xmpData = getEmbeddableAdjustments(exported.adjustments, imageInfo)
						if (photo.rating != null && photo.rating > 0) {
							xmpData["xmp:Rating"] = photo.rating
						}
						if (isRealColorTag(photo.colorTag)) {
							xmpData["xmp:Label"] = photo.colorTag
						}

						const encodeTask = encodeQueue.add(async () => {
							let didScheduleUpload = false
							try {
								session.throwIfCanceled()
								const encodedImageData = await encodeJpegBytes(exported.imageData, {
									...options,
									metadata: exported.metadata,
									jpegEncoder: exported.leaseForEncode ?? JpegEncoder,
									xmpData,
								})
								session.throwIfCanceled()
								if (isValidatedExportType(options.type)) {
									validateEncodedExportSizeAndReport({
										format: options.type,
										encoded: encodedImageData.bytes,
										width: exported.imageData.width,
										height: exported.imageData.height,
										quality: options.quality,
										iso: exported.metadata?.iso,
										bitDepth:
											exported.imageData.data instanceof Uint16Array ? 16 : 8,
										photoId: exported.metadata?.photoId,
										colorSpace: exported.imageData.colorSpace,
										sourceFileSize: exported.metadata?.size,
										sourceFileExtension: exported.metadata?.extension,
									})
								}
								if (
									isPixiesetJpegOverUploadLimit(encodedImageData.bytes.byteLength)
								) {
									oversizeHandler({ filename, photoId: photo.id })
									return
								}

								schedulePublishUpload(
									photo,
									filename,
									rank,
									encodedImageData,
									pipelineLease,
								)
								didScheduleUpload = true
							} finally {
								await session.releaseLease(exported.leaseForEncode)
								if (!didScheduleUpload) {
									await session.releaseLease(pipelineLease)
								}
							}
						})
						didQueueEncode = true
						didTransferPipelineLease = true
						void encodeTask.catch((err) => {
							handlePublishPhotoError(photo, err)
						})
					} finally {
						if (!didQueueEncode) {
							await session.releaseLease(exported.leaseForEncode)
						}
					}
				} finally {
					if (!didTransferPipelineLease) {
						await session.releaseLease(pipelineLease)
					}
				}
			})
			.catch((err) => {
				handlePublishPhotoError(photo, err)
			})
	})

	return { queue, encodeQueue, uploadQueue }
}

const storePhotos = async (
	successUploads: UploadResult[],
	galleryId: string,
	publishExportSettingsKey: string,
) => {
	const storeRequest: StorePhotosRequestDTO = {
		files: successUploads.map((result) => ({
			name: result.filename,
			path: getPhotoKey(result.filename, galleryId),
			type: "image/jpeg",
			size: result.size,
			rank: result.rank,
		})),
	}
	await PSApiOperations.storePhotos({
		galleryId,
		requestBody: storeRequest,
	})
	const publishedDate = Date.now()
	const allPhotos = store.get(photosAtom)
	await updatePhotos((photos) => {
		for (const result of successUploads) {
			const existingDates = allPhotos[result.photoId]?.publishedDates ?? {}
			const existingExportSettings = allPhotos[result.photoId]?.publishedExportSettings ?? {}
			const updatedDates = { ...existingDates, [galleryId]: publishedDate }
			photos[result.photoId] = {
				publishedDates: updatedDates,
				publishedExportSettings: {
					...existingExportSettings,
					[galleryId]: publishExportSettingsKey,
				},
			}
		}
	})
}
